import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AdminCreatorsTab = () => {
  // Claims
  const [claims, setClaims] = useState([]);
  const [claimsTotal, setClaimsTotal] = useState(0);
  const [claimsPage, setClaimsPage] = useState(1);
  const [claimsFilter, setClaimsFilter] = useState('all');
  const [claimsSearch, setClaimsSearch] = useState('');
  const [expandedClaim, setExpandedClaim] = useState(null);
  const [claimLoading, setClaimLoading] = useState(false);

  // Manual link
  const [linkAccountId, setLinkAccountId] = useState('');
  const [linkChannelId, setLinkChannelId] = useState('');
  const [linkMessage, setLinkMessage] = useState('');

  // Reports
  const [reports, setReports] = useState([]);
  const [reportsFilter, setReportsFilter] = useState('all');
  const [resolutionText, setResolutionText] = useState({});

  // Verified creators
  const [creators, setCreators] = useState([]);

  // Section toggle
  const [activeSection, setActiveSection] = useState('claims');

  // Alert
  const [alert, setAlert] = useState(null);

  const showAlert = (msg, type = 'success') => {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 4000);
  };

  // Fetch claims
  const fetchClaims = async () => {
    setClaimLoading(true);
    try {
      const params = new URLSearchParams({ page: claimsPage, limit: 15 });
      if (claimsFilter !== 'all') params.append('status', claimsFilter);
      if (claimsSearch) params.append('search', claimsSearch);
      const res = await axios.get(`${API_URL}/admin/creator/claims?${params}`, { headers: getAuthHeaders() });
      setClaims(res.data.claims || []);
      setClaimsTotal(res.data.total || 0);
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to fetch claims', 'error');
    } finally {
      setClaimLoading(false);
    }
  };

  // Fetch reports
  const fetchReports = async () => {
    try {
      const params = new URLSearchParams();
      if (reportsFilter !== 'all') params.append('status', reportsFilter);
      const res = await axios.get(`${API_URL}/admin/creator/reports?${params}`, { headers: getAuthHeaders() });
      setReports(res.data.reports || []);
    } catch (err) {
      showAlert('Failed to fetch reports', 'error');
    }
  };

  // Fetch verified creators
  const fetchCreators = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/creator/list`, { headers: getAuthHeaders() });
      setCreators(res.data.creators || []);
    } catch (err) {
      showAlert('Failed to fetch creators', 'error');
    }
  };

  useEffect(() => {
    fetchClaims();
  }, [claimsPage, claimsFilter]);

  useEffect(() => {
    if (activeSection === 'reports') fetchReports();
    if (activeSection === 'creators') fetchCreators();
  }, [activeSection, reportsFilter]);

  // Actions
  const handleApproveClaim = async (id) => {
    const note = prompt('Admin note (optional):');
    try {
      await axios.put(`${API_URL}/admin/creator/claims/${id}/approve`, { note }, { headers: getAuthHeaders() });
      showAlert('Claim approved');
      fetchClaims();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to approve', 'error');
    }
  };

  const handleRejectClaim = async (id) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    try {
      await axios.put(`${API_URL}/admin/creator/claims/${id}/reject`, { reason }, { headers: getAuthHeaders() });
      showAlert('Claim rejected');
      fetchClaims();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to reject', 'error');
    }
  };

  const handleRevokeClaim = async (id) => {
    if (!window.confirm('Revoke this claim? This will unlink the channel.')) return;
    try {
      await axios.delete(`${API_URL}/admin/creator/claims/${id}/revoke`, { headers: getAuthHeaders() });
      showAlert('Claim revoked');
      fetchClaims();
      if (activeSection === 'creators') fetchCreators();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to revoke', 'error');
    }
  };

  const handleManualLink = async () => {
    if (!linkAccountId.trim() || !linkChannelId.trim()) {
      setLinkMessage('Both fields required');
      return;
    }
    try {
      await axios.put(`${API_URL}/admin/creator/manual-link`, {
        accountId: linkAccountId.trim(),
        channelId: linkChannelId.trim()
      }, { headers: getAuthHeaders() });
      setLinkMessage('Linked successfully!');
      setLinkAccountId('');
      setLinkChannelId('');
      fetchClaims();
    } catch (err) {
      setLinkMessage(err.response?.data?.error || 'Failed to link');
    }
  };

  const handleResolveReport = async (id, dismiss = false) => {
    try {
      await axios.put(`${API_URL}/admin/creator/reports/${id}/resolve`, {
        resolution: resolutionText[id] || '',
        dismiss
      }, { headers: getAuthHeaders() });
      showAlert(`Report ${dismiss ? 'dismissed' : 'resolved'}`);
      fetchReports();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed', 'error');
    }
  };

  // Styles
  const s = {
    container: { padding: '0' },
    alert: (type) => ({
      padding: '10px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '13px',
      fontWeight: '500',
      background: type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
      color: type === 'error' ? '#EF4444' : '#10B981',
      border: `1px solid ${type === 'error' ? '#EF444433' : '#10B98133'}`,
    }),
    sectionNav: {
      display: 'flex',
      gap: '4px',
      marginBottom: '20px',
      background: '#1e1e2e',
      padding: '4px',
      borderRadius: '8px',
      width: 'fit-content',
    },
    sectionBtn: (active) => ({
      padding: '8px 18px',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: active ? '600' : '400',
      background: active ? '#8B5CF6' : 'transparent',
      color: active ? '#FFF' : '#999',
    }),
    filterBar: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    filterBtn: (active) => ({
      padding: '6px 14px',
      border: 'none',
      borderRadius: '16px',
      cursor: 'pointer',
      fontSize: '12px',
      background: active ? '#8B5CF6' : '#2a2a3e',
      color: active ? '#FFF' : '#AAA',
    }),
    searchInput: {
      padding: '6px 14px',
      background: '#1a1a2e',
      border: '1px solid #333',
      borderRadius: '16px',
      color: '#E5E5E5',
      fontSize: '12px',
      outline: 'none',
      width: '200px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px',
    },
    th: {
      textAlign: 'left',
      padding: '10px 12px',
      borderBottom: '1px solid #333',
      color: '#888',
      fontWeight: '600',
      fontSize: '11px',
      textTransform: 'uppercase',
    },
    td: {
      padding: '10px 12px',
      borderBottom: '1px solid #222',
      color: '#CCC',
      verticalAlign: 'middle',
    },
    statusBadge: (status) => {
      const colors = {
        pending: '#F59E0B', code_issued: '#3B82F6', verified: '#10B981',
        admin_approved: '#10B981', failed: '#EF4444', revoked: '#EF4444',
        reviewing: '#3B82F6', resolved: '#10B981', dismissed: '#999',
      };
      return {
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        background: `${colors[status] || '#888'}20`,
        color: colors[status] || '#888',
      };
    },
    actionBtn: (color) => ({
      padding: '4px 12px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '11px',
      fontWeight: '600',
      background: `${color}20`,
      color: color,
      marginRight: '4px',
    }),
    card: {
      background: '#1a1a2e',
      borderRadius: '8px',
      border: '1px solid #2a2a3e',
      padding: '16px',
      marginBottom: '16px',
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      background: '#0f0f1e',
      border: '1px solid #333',
      borderRadius: '6px',
      color: '#E5E5E5',
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
      marginBottom: '8px',
    },
    primaryBtn: (disabled) => ({
      padding: '8px 20px',
      border: 'none',
      borderRadius: '6px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      background: disabled ? '#333' : '#8B5CF6',
      color: '#FFF',
      opacity: disabled ? 0.6 : 1,
    }),
    expandedRow: {
      background: '#1a1a2e',
      padding: '12px',
      borderRadius: '6px',
      margin: '4px 0 8px',
      fontSize: '12px',
      color: '#AAA',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '16px',
    },
    pageBtn: (disabled) => ({
      padding: '6px 14px',
      border: '1px solid #333',
      borderRadius: '4px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '12px',
      background: disabled ? '#1a1a2e' : '#2a2a3e',
      color: disabled ? '#555' : '#CCC',
    }),
  };

  const totalPages = Math.ceil(claimsTotal / 15);

  return (
    <div style={s.container}>
      {alert && <div style={s.alert(alert.type)}>{alert.msg}</div>}

      <div style={s.sectionNav}>
        {['claims', 'manual', 'reports', 'creators'].map(sec => (
          <button key={sec} style={s.sectionBtn(activeSection === sec)} onClick={() => setActiveSection(sec)}>
            {sec === 'claims' ? 'Claims Queue' : sec === 'manual' ? 'Manual Link' : sec === 'reports' ? 'Reports' : 'Verified Creators'}
          </button>
        ))}
      </div>

      {/* CLAIMS QUEUE */}
      {activeSection === 'claims' && (
        <>
          <div style={s.filterBar}>
            {['all', 'pending', 'code_issued', 'verified', 'admin_approved', 'failed', 'revoked'].map(f => (
              <button key={f} style={s.filterBtn(claimsFilter === f)} onClick={() => { setClaimsFilter(f); setClaimsPage(1); }}>
                {f === 'all' ? 'All' : f.replace(/_/g, ' ')}
              </button>
            ))}
            <input
              style={s.searchInput}
              placeholder="Search channel..."
              value={claimsSearch}
              onChange={e => setClaimsSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchClaims()}
            />
          </div>

          {claimLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
          ) : claims.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No claims found</div>
          ) : (
            <>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Creator</th>
                    <th style={s.th}>Channel</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Attempts</th>
                    <th style={s.th}>Created</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map(claim => (
                    <React.Fragment key={claim._id}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedClaim(expandedClaim === claim._id ? null : claim._id)}>
                        <td style={s.td}>{claim.accountId?.username || 'Unknown'}</td>
                        <td style={s.td}>
                          <div style={{ fontWeight: '500' }}>{claim.channelName || '—'}</div>
                          <div style={{ fontSize: '11px', color: '#666' }}>{claim.youtubeChannelId}</div>
                        </td>
                        <td style={s.td}><span style={s.statusBadge(claim.status)}>{claim.status?.replace(/_/g, ' ')}</span></td>
                        <td style={s.td}>{claim.attempts || 0}</td>
                        <td style={s.td}>{new Date(claim.createdAt).toLocaleDateString()}</td>
                        <td style={s.td} onClick={e => e.stopPropagation()}>
                          {['pending', 'code_issued', 'failed'].includes(claim.status) && (
                            <button style={s.actionBtn('#10B981')} onClick={() => handleApproveClaim(claim._id)}>Approve</button>
                          )}
                          {['pending', 'code_issued'].includes(claim.status) && (
                            <button style={s.actionBtn('#EF4444')} onClick={() => handleRejectClaim(claim._id)}>Reject</button>
                          )}
                          {['verified', 'admin_approved'].includes(claim.status) && (
                            <button style={s.actionBtn('#F59E0B')} onClick={() => handleRevokeClaim(claim._id)}>Revoke</button>
                          )}
                        </td>
                      </tr>
                      {expandedClaim === claim._id && (
                        <tr>
                          <td colSpan="6" style={{ padding: '0 12px 8px' }}>
                            <div style={s.expandedRow}>
                              <div><strong>Verification Code:</strong> {claim.verificationCode || '—'}</div>
                              <div><strong>Method:</strong> {claim.verificationMethod || '—'}</div>
                              <div><strong>Code Expires:</strong> {claim.codeExpiresAt ? new Date(claim.codeExpiresAt).toLocaleString() : '—'}</div>
                              <div><strong>Verified At:</strong> {claim.verifiedAt ? new Date(claim.verifiedAt).toLocaleString() : '—'}</div>
                              <div><strong>Admin Note:</strong> {claim.adminNote || '—'}</div>
                              {claim.attemptLog?.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                  <strong>Attempt Log:</strong>
                                  {claim.attemptLog.map((log, i) => (
                                    <div key={i} style={{ marginLeft: '12px', fontSize: '11px', color: '#888' }}>
                                      {new Date(log.attemptedAt).toLocaleString()} — {log.result} ({log.checkedIn})
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div style={s.pagination}>
                  <button style={s.pageBtn(claimsPage <= 1)} onClick={() => setClaimsPage(p => p - 1)} disabled={claimsPage <= 1}>Prev</button>
                  <span style={{ color: '#888', fontSize: '13px', padding: '6px' }}>{claimsPage} / {totalPages}</span>
                  <button style={s.pageBtn(claimsPage >= totalPages)} onClick={() => setClaimsPage(p => p + 1)} disabled={claimsPage >= totalPages}>Next</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* MANUAL LINK */}
      {activeSection === 'manual' && (
        <div style={s.card}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#FFF', marginBottom: '16px' }}>Manual Link Account to Channel</div>
          <input style={s.input} placeholder="Account ID" value={linkAccountId} onChange={e => setLinkAccountId(e.target.value)} />
          <input style={s.input} placeholder="YouTube Channel ID (UCxxxxxxx)" value={linkChannelId} onChange={e => setLinkChannelId(e.target.value)} />
          <button style={s.primaryBtn(!linkAccountId || !linkChannelId)} onClick={handleManualLink} disabled={!linkAccountId || !linkChannelId}>
            Link Account
          </button>
          {linkMessage && <div style={{ marginTop: '8px', fontSize: '13px', color: linkMessage.includes('success') ? '#10B981' : '#EF4444' }}>{linkMessage}</div>}
        </div>
      )}

      {/* REPORTS */}
      {activeSection === 'reports' && (
        <>
          <div style={s.filterBar}>
            {['all', 'pending', 'reviewing', 'resolved', 'dismissed'].map(f => (
              <button key={f} style={s.filterBtn(reportsFilter === f)} onClick={() => setReportsFilter(f)}>
                {f === 'all' ? 'All' : f}
              </button>
            ))}
          </div>

          {reports.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No reports found</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Reporter</th>
                  <th style={s.th}>Type</th>
                  <th style={s.th}>Target</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Date</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map(r => (
                  <tr key={r._id}>
                    <td style={s.td}>{r.reporterId?.username || 'Unknown'}</td>
                    <td style={s.td}>{r.reportType?.replace(/_/g, ' ')}</td>
                    <td style={s.td}>
                      <div>{r.targetType}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>{r.targetId}</div>
                    </td>
                    <td style={s.td}><span style={s.statusBadge(r.status)}>{r.status}</span></td>
                    <td style={s.td}>{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td style={s.td}>
                      {r.status === 'pending' || r.status === 'reviewing' ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <input
                            style={{ ...s.searchInput, width: '120px', marginBottom: 0 }}
                            placeholder="Resolution..."
                            value={resolutionText[r._id] || ''}
                            onChange={e => setResolutionText(prev => ({ ...prev, [r._id]: e.target.value }))}
                            onClick={e => e.stopPropagation()}
                          />
                          <button style={s.actionBtn('#10B981')} onClick={() => handleResolveReport(r._id)}>Resolve</button>
                          <button style={s.actionBtn('#999')} onClick={() => handleResolveReport(r._id, true)}>Dismiss</button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#666' }}>{r.resolution || '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* VERIFIED CREATORS */}
      {activeSection === 'creators' && (
        <>
          {creators.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No verified creators yet</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Creator</th>
                  <th style={s.th}>Channel</th>
                  <th style={s.th}>Channel ID</th>
                  <th style={s.th}>Verified</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {creators.map(c => (
                  <tr key={c._id}>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {c.accountId?.avatar && <img src={c.accountId.avatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />}
                        {c.accountId?.username || 'Unknown'}
                      </div>
                    </td>
                    <td style={s.td}>{c.channelName}</td>
                    <td style={s.td} title={c.claimedChannelId}>{c.claimedChannelId ? c.claimedChannelId.substring(0, 12) + '...' : '—'}</td>
                    <td style={s.td}>{c.verificationDate ? new Date(c.verificationDate).toLocaleDateString() : '—'}</td>
                    <td style={s.td}>
                      {c.claimId && (
                        <button style={s.actionBtn('#EF4444')} onClick={() => handleRevokeClaim(c.claimId)}>Revoke</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default AdminCreatorsTab;
